/* Billy product showcase + product page: gallery arrows/thumbnails, product video, bundle tiers
   (Solo/Duo/Trio), one colour drop-down per tote and multi-item add to cart via the Cart AJAX
   API. Simple products (accessories) use the native form plus a quantity stepper.
   Without JS the gallery still scrolls and the form adds a single item natively. */
(() => {
  const init = (root) => {
    if (!root || root.dataset.billyReady) return;
    root.dataset.billyReady = 'true';

    // ----- Gallery -----
    const gallery = root.querySelector('[data-billy-gallery]');
    const track = gallery && gallery.querySelector('[data-gallery-track]');
    if (track) {
      const slides = Array.from(track.children);
      const thumbs = Array.from(gallery.querySelectorAll('[data-thumb]'));
      const prev = gallery.querySelector('[data-gallery-prev]');
      const next = gallery.querySelector('[data-gallery-next]');
      const thumbsWrap = gallery.querySelector('[data-thumbs]');
      const thumbsTrack = gallery.querySelector('[data-thumbs-track]');
      let index = 0;

      const goTo = (i) => {
        index = Math.max(0, Math.min(slides.length - 1, i));
        track.scrollTo({ left: track.clientWidth * index });
      };

      const sync = () => {
        const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        if (i === index && thumbs[i] && thumbs[i].getAttribute('aria-current') === 'true') return;
        index = i;
        thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
        if (prev) prev.disabled = i <= 0;
        if (next) next.disabled = i >= slides.length - 1;
        const active = thumbs[i];
        if (active && thumbsTrack) {
          const left = active.offsetLeft - (thumbsTrack.clientWidth - active.offsetWidth) / 2;
          thumbsTrack.scrollTo({ left });
        }
      };

      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { sync(); ticking = false; });
      }, { passive: true });

      if (prev) prev.addEventListener('click', () => goTo(index - 1));
      if (next) next.addEventListener('click', () => goTo(index + 1));
      thumbs.forEach((t) => t.addEventListener('click', () => goTo(Number(t.dataset.thumb))));

      if (thumbsWrap && thumbsTrack) {
        const step = () => thumbsTrack.clientWidth * 0.8;
        gallery.querySelector('[data-thumbs-prev]').addEventListener('click', () => thumbsTrack.scrollBy({ left: -step() }));
        gallery.querySelector('[data-thumbs-next]').addEventListener('click', () => thumbsTrack.scrollBy({ left: step() }));
        const checkOverflow = () => thumbsWrap.classList.toggle('has-overflow', thumbsTrack.scrollWidth > thumbsTrack.clientWidth + 2);
        checkOverflow();
        if ('ResizeObserver' in window) new ResizeObserver(checkOverflow).observe(thumbsTrack);
      }

      sync();

      // Product video (first slide): autoplays muted; pause/play toggle; pauses when off-screen.
      const video = track.querySelector('[data-video-slide] video');
      if (video) {
        const toggle = track.querySelector('[data-video-toggle]');
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let userPaused = reduce;
        const setState = () => {
          if (!toggle) return;
          toggle.classList.toggle('is-paused', video.paused);
          toggle.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
        };
        video.muted = true;
        if (reduce) video.pause();
        const tryPlay = () => { const p = video.play(); if (p && p.catch) p.catch(() => setState()); };
        if (toggle) toggle.addEventListener('click', () => {
          if (video.paused) { userPaused = false; tryPlay(); } else { userPaused = true; video.pause(); }
        });
        video.addEventListener('play', setState);
        video.addEventListener('pause', setState);
        if ('IntersectionObserver' in window) {
          new IntersectionObserver((entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting && e.intersectionRatio > 0.5) { if (!userPaused) tryPlay(); }
              else video.pause();
            });
          }, { threshold: [0, 0.5, 1] }).observe(video);
        } else if (!userPaused) tryPlay();
        setState();
      }

      root.billyGoToMedia = (mediaId) => {
        const n = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
        if (n > -1) goTo(n);
      };
    }

    // ----- Buy box: bundle tiers, per-tote colours, add to cart -----
    const buy = root.querySelector('[data-billy-buy]');
    if (!buy) return;

    const qtyWrap = buy.querySelector('[data-qty]');
    if (qtyWrap) {
      const input = qtyWrap.querySelector('input');
      qtyWrap.querySelectorAll('[data-qty-step]').forEach((b) => b.addEventListener('click', () => {
        const v = Math.max(1, (parseInt(input.value, 10) || 1) + Number(b.dataset.qtyStep));
        input.value = v;
      }));
    }
    const form = buy.querySelector('form');
    const stock = buy.querySelector('[data-stock]');
    const total = buy.querySelector('[data-total]');
    const totalLabel = buy.querySelector('[data-total-label]');
    const errorBox = buy.querySelector('[data-error]');
    const submit = buy.querySelector('button[type="submit"]');
    const totes = Array.from(buy.querySelectorAll('.billy-tote[data-tote-index]'));
    const inStockText = stock ? stock.dataset.labelIn : '';

    // Simple products (no per-tote pickers): keep the native form; just reflect the chosen variant.
    if (!totes.length) {
      const price = root.querySelector('[data-price]');
      buy.addEventListener('change', (e) => {
        const r = e.target;
        if (r.type !== 'radio' || r.name !== 'id') return;
        if (price && r.dataset.price) price.textContent = r.dataset.price;
        if (submit) submit.disabled = r.dataset.available !== 'true';
        if (r.dataset.mediaId && root.billyGoToMedia) root.billyGoToMedia(r.dataset.mediaId);
      });
      return;
    }


    const currentTier = () => buy.querySelector('input[data-tier-qty]:checked');
    const tierQty = () => {
      const t = currentTier();
      return t ? Number(t.dataset.tierQty) || 1 : 1;
    };
    // The chosen colour for one tote: the selected <option> (drop-down) or checked radio (fallback).
    const choice = (fs) => {
      const sel = fs.querySelector('select');
      if (sel) return sel.selectedOptions[0] || null;
      return fs.querySelector('input[type="radio"]:checked');
    };

    const show = (el, on) => {
      el.hidden = !on;
      el.disabled = !on;
    };

    const refresh = () => {
      const qty = tierQty();
      totes.forEach((fs) => show(fs, Number(fs.dataset.toteIndex) <= qty));

      const chosen = totes
        .filter((fs) => !fs.disabled)
        .map(choice);
      const allAvailable = chosen.every((i) => i && i.dataset.available === 'true');

      if (stock) {
        stock.dataset.available = allAvailable;
        stock.textContent = allAvailable ? inStockText : 'Sold out';
      }
      if (submit) submit.disabled = !allAvailable;

      const t = currentTier();
      if (total) total.textContent = (t && t.dataset.tierPrice) || (chosen[0] && chosen[0].dataset.price) || total.textContent;
      if (totalLabel) {
        const count = qty;
        totalLabel.textContent = count === 1
          ? totalLabel.dataset.one
          : totalLabel.dataset.many.replace('[count]', count);
      }
      if (errorBox) errorBox.hidden = true;
    };

    buy.addEventListener('change', (e) => {
      const input = e.target;
      const fs = input.closest('.billy-tote');
      if (fs) {
        const opt = choice(fs);
        const dot = fs.querySelector('[data-dot]');
        if (dot && opt && opt.dataset.swatch) dot.style.setProperty('--swatch', opt.dataset.swatch);
        if (opt && opt.dataset.mediaId && root.billyGoToMedia) root.billyGoToMedia(opt.dataset.mediaId);
      }
      refresh();
    });

    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const qty = tierQty();
        const counts = new Map();
        totes.slice(0, qty).forEach((fs) => {
          const r = choice(fs);
          if (r) counts.set(r.value, (counts.get(r.value) || 0) + 1);
        });
        const items = Array.from(counts, ([id, quantity]) => ({ id: Number(id), quantity }));
        if (!items.length) return;

        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        try {
          const res = await fetch(`${buy.dataset.cartAddUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(window.BillyCart
              ? { items, sections: window.BillyCart.sectionIds(), sections_url: window.location.pathname }
              : { items }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
          if (window.BillyCart) {
            window.BillyCart.added(data);
            submit.disabled = false;
            submit.removeAttribute('aria-busy');
          } else {
            window.location.href = buy.dataset.cartUrl;
          }
        } catch (err) {
          if (errorBox) {
            errorBox.textContent = err.message;
            errorBox.hidden = false;
          }
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
        }
      });
    }

    refresh();
  };

  const boot = () => document.querySelectorAll('.billy-showcase, [data-billy-product]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
